import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
function Economics31() {
   const contentRef = useRef(null);
         const links = {
        prev: "/economics-30", 
        index: "/index-economics",                 
        next: "/economics-32", 
      };
        const titleText ="The Illusion of Credit-Driven Productivity";

const questions = [
  {
    text: "1. According to the lesson, under what condition does credit genuinely enhance productivity?",
    options: [
      {
        value: "A",
        label: "A. When credit is distributed equally across society",
      },
      {
        value: "B",
        label: "B. When credit is allocated through market-based risk and return evaluation",
      },
      {
        value: "C",
        label: "C. When governments guarantee loan repayment",
      },
      {
        value: "D",
        label: "D. When interest rates are kept artificially low",
      },
    ],
    correct: "B",
  },
  {
    text: "2. What is the main economic problem caused by government-backed credit programs?",
    options: [
      {
        value: "A",
        label: "A. Excessive competition among private lenders",
      },
      {
        value: "B",
        label: "B. Higher interest rates for all borrowers",
      },
      {
        value: "C",
        label: "C. Misallocation of scarce resources to less productive users",
      },
      {
        value: "D",
        label: "D. Reduced availability of physical capital",
      },
    ],
    correct: "C",
  },
  {
    text: "3. How does government intervention in credit markets distort market signals?",
    options: [
      {
        value: "A",
        label: "A. By increasing transparency in lending decisions",
      },
      {
        value: "B",
        label: "B. By removing the role of risk assessment in lending",
      },
      {
        value: "C",
        label: "C. By encouraging lenders to rely more on consumer demand",
      },
      {
        value: "D",
        label: "D. By eliminating profit and loss considerations entirely",
      },
    ],
    correct: "B",
  },
  {
    text: "4. Why does credit expansion often encourage short-term decision-making?",
    options: [
      {
        value: "A",
        label: "A. Because borrowers prioritize immediate repayment pressures over long-term productivity",
      },
      {
        value: "B",
        label: "B. Because long-term investments are legally restricted",
      },
      {
        value: "C",
        label: "C. Because markets discourage future-oriented planning",
      },
      {
        value: "D",
        label: "D. Because productivity gains only occur in the short run",
      },
    ],
    correct: "A",
  },
];

  // State to track selected answers for all questions
      const [answers, setAnswers] = useState({});
    
      // Function to update an answer
      const handleAnswerChange = (questionIndex, selectedValue) => {
        setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
      };
    
      // Compute score
      const score = questions.reduce((total, q, index) => {
        return total + (answers[index] === q.correct ? 1 : 0);
      }, 0);


  return (
    <div className="container">
    
 <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={31}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />
      

      <p className="content" ref={contentRef}>Welcome to <b>Day 31</b>. Today’s lesson examines the widely held belief that credit is a reliable engine of productivity and economic growth. While credit is often promoted as a solution to funding gaps and a catalyst for development, this session challenges that assumption by exploring how credit functions in practice, especially when influenced by government intervention. You will learn why credit expansions frequently misallocate resources, distort market signals, and ultimately reduce long-term productivity rather than enhance it.
<br/><br/>
        <b>The Intended Role of Credit in Economic Growth</b><br/><br/>
        Credits have long been considered a tool for economic growth and productivity enhancement. Governments and financial institutions often promote credit as a way to fuel economic activity, arguing that it enables individuals and businesses to make investments they otherwise could not afford. In theory, credit allows borrowers to access resources today in order to create greater value in the future. If this value exceeds the cost of borrowing, credit appears to support growth, innovation, and wealth creation.
<br/>At its core, credit is simply the borrowing of resources, whether money or capital, for future economic activity. The assumption behind productive credit is that lenders allocate capital to borrowers who are most capable of using it efficiently and repaying it. This allocation process relies on evaluating risk and expected return, which helps direct scarce resources toward their most productive uses. When credit follows this market-driven process, it can play a constructive role in economic progress.
<br/>However, this framework depends entirely on credit being allocated through voluntary market mechanisms. When credit allocation is influenced by non-market factors, such as government intervention or political objectives, the link between credit and productivity begins to break down. Instead of flowing to the most efficient users, capital may be diverted toward less productive uses, reducing overall economic welfare.
   <br/><br/>
   <b>Government-Backed Credit and the Misallocation of Resources</b> <br/><br/>
   One of the most common forms of credit that fails to enhance productivity is government-backed loans and subsidies. Governments often justify their involvement in lending by claiming they can extend credit to individuals or businesses excluded from private markets due to low creditworthiness, lack of collateral, or insufficient financial history. The stated goal is usually social improvement or economic inclusion, with the expectation that these borrowers will eventually contribute to productivity and growth.
<br/>In practice, government-backed credit operates very differently from private lending. Private lenders risk their own capital and therefore have strong incentives to carefully assess borrowers. Government lenders, by contrast, risk taxpayer resources rather than their own. This weakens the discipline of risk evaluation and leads to loans being issued to borrowers who may not be capable of using capital productively or repaying it. While some borrowers may succeed, many do not, resulting in wasted resources and lower productivity.
<br/>The fundamental problem is misallocation. Capital such as land, labor, and machinery is scarce. When government credit directs these resources to less capable users, it necessarily deprives more efficient users of access. Consider two farmers. One has experience, savings, and a record of profitability, making him attractive to private lenders. The other lacks experience, savings, and proven ability, and can only obtain credit through a government program. When government-backed credit allows the less capable farmer to acquire land, the more productive farmer is excluded. The result is lower output and reduced efficiency, even though economic activity appears to increase.
<br/>This pattern applies broadly across the economy. Government credit may produce visible successes, but these are often outweighed by unseen losses from forgone productive opportunities. The economy ends up with fewer goods, lower productivity, and diminished wealth creation.
  <br/><br/><b>Distorted Market Signals and Long-Term Economic Costs</b> <br/><br/>
  <br/>Beyond misallocating resources, government-backed credit also distorts market signals. In a functioning market, lending decisions reflect real assessments of risk and expected return. These signals help coordinate economic activity by guiding capital toward ventures most likely to succeed. Government intervention weakens these signals by lowering perceived risk for borrowers who would otherwise be denied credit.
<br/>As a result, private lenders may become less cautious, unsustainable borrowing increases, and capital flows into projects that would not survive under market conditions. Political considerations further distort credit allocation, as loans may be directed toward favored groups or sectors rather than economically sound opportunities. This undermines the market’s ability to coordinate resources effectively.
<br/>Credit expansion also encourages short-term thinking. Borrowers facing repayment pressures may prioritize immediate cash flow over long-term productivity improvements. This tendency is especially pronounced when loans are perceived as government assistance rather than market-based investments. The focus shifts from sustainable growth to short-term survival, preventing lasting productivity gains.
<br/>In addition to economic inefficiencies, credit expansion carries political and social costs. Government lending can foster cronyism, favoritism, and corruption, as access to credit becomes tied to political influence rather than economic merit. When loans fail, the burden falls on taxpayers through higher taxes or reduced public services, compounding the damage caused by misallocation.
<br/>In conclusion, while credit can support productivity when allocated through market-driven processes, government-backed credit often produces the opposite effect. By diverting resources, distorting signals, and encouraging inefficient behavior, such credit reduces overall productivity and long-term economic welfare. Sustainable growth requires that credit be guided by sound economic principles and voluntary market forces, not political intervention or misguided attempts to stimulate activity.
   <br/><br/><b>Summary of Today's Lesson</b><br/><br/>This lesson explains why credit, despite appearing to promote growth, often fails to enhance productivity when driven by government intervention rather than market forces. It shows that productive credit depends on risk-based allocation that directs capital to its most efficient users, while government-backed lending misallocates resources, distorts market signals, and encourages short-term decision-making. As a result, such credit expansion reduces overall economic welfare instead of creating sustainable growth.
<br/><br/><b>Coming Up Next</b><br/><br/>
On Day 32, we will turn our attention to one of the most emotionally charged and misunderstood claims in economics: the fear that machines and technological progress destroy human jobs. Building on what you have learned so far about productivity, capital, and market coordination, the next lesson will show why this fear is rooted in short-term thinking and a fixed-view of the economy. You will explore how innovation reshapes labor rather than eliminates it, how machines expand human potential, and why technological progress has historically led to higher wages, new industries, and improved standards of living rather than mass unemployment.<br/><br/>
     </p>
      <h2>Test your understanding</h2>
       {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics31;
